import cn from 'classnames';
import React from 'react';
import { useTableColumnsContext, TableColumn } from './TableColumns';
import TableCell from './TableCell';

interface TableRowProps {
    index: number | null;
    isLoading?: boolean;
    onHover?: (index: number | null) => void;
    isSticky?: boolean;
    style?: React.CSSProperties;
    className?: string;
    children?: React.ReactNode;
    testId?: string;
}

const TableRow: React.FC<TableRowProps> = ({
    index,
    isLoading,
    isSticky,
    style,
    className,
    onHover,
    children,
    testId,
}) => {
    const { data } = useTableColumnsContext();

    const renderColumn = (col: TableColumn, colIndex: number, level: number = 0): React.ReactNode => {
        const hasSub = Array.isArray(col.subColumns) && col.subColumns.length > 0;

        return (
            <TableCell
                key={`${col.key}-${colIndex}`}
                isSub={level > 0}
                isLoading={isLoading && !hasSub}
                hasSub={hasSub}
                style={col.style?.cell}
                testId={`tableCell${index}_${colIndex}_${level}`}
                accentLevel={(isSticky && col.stickyConfig?.accentLevel) || col.accentLevel}
                {...col.getHtmlAttributes?.(index ?? undefined)}
            >
                {hasSub ? (
                    <div className="TableCellColumns">
                        {(col.subColumns ?? []).map((subCol, subIndex) =>
                            renderColumn(subCol, subIndex, level + 1)
                        )}
                    </div>
                ) : (
                    index !== null && !isLoading && (
                        <>
                            {isSticky && col.stickyConfig?.Value ? (
                                <col.stickyConfig.Value index={index} />
                            ) : (
                                <col.Value index={index} />
                            )}
                        </>
                    )
                )}
            </TableCell>
        );
    };

    return (
        <div
            className={cn('TableRow', className)}
            style={style}
            data-testid={testId}
            onMouseEnter={() => onHover?.(index)}
            onMouseLeave={() => onHover?.(null)}
        >
            {data.map((col, colIndex) => renderColumn(col, colIndex, 0))}
            {children}
        </div>
    );
};

export default TableRow;
